import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@components/context/AuthContext";
import { useTranslation } from "react-i18next";
import LanguageToggle from "@components/constant_components/LanguageToggle";

interface UserAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userName?: string;
  userInitials: string;
}

export default function UserAccountModal({
  isOpen,
  onClose,
  userEmail,
  userName,
  userInitials,
}: UserAccountModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSignOut = () => {
    onClose();
    navigate("/logout");
  };

  const handleManageAccount = () => {
    onClose();
    if (user?.user_id) {
      navigate(`/contractor/${user.user_id}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end tw-items-start tw-pt-14 tw-pr-4">
      <div
        ref={modalRef}
        className="tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-w-[420px] tw-p-6 tw-relative"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-500 hover:tw-text-gray-700 tw-text-2xl tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center"
          aria-label="Close"
        >
          ×
        </button>

        {/* Email section */}
        <div className="tw-text-center tw-mb-4">
          <h2 className="tw-text-lg tw-font-normal tw-text-gray-800">
            {userEmail || user?.email || ""}
          </h2>
          <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
            {t("userAccountModal.managedBy")}
          </p>
        </div>

        {/* Language Toggle */}
        <div className="tw-flex tw-justify-end tw-mb-4">
          <LanguageToggle compact={false} />
        </div>

        {/* Avatar and greeting */}
        <div className="tw-flex tw-flex-col tw-items-center tw-my-6">
          <div className="tw-relative tw-mb-4">
            <div className="tw-w-24 tw-h-24 tw-rounded-full tw-bg-gray-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-3xl tw-font-medium">
              {userInitials}
            </div>
            {/* <button className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-white tw-rounded-full tw-p-1.5 tw-shadow-md hover:tw-bg-gray-100">
              <svg
                className="tw-w-5 tw-h-5 tw-text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button> */}
          </div>
          <h3 className="tw-text-3xl tw-font-normal tw-text-gray-900">
            {t("userAccountModal.greeting", {
              name: userName || user?.user_name || "User",
            })}
          </h3>
        </div>

        {/* Manage account button */}
        <div className="tw-flex tw-justify-center tw-mb-6">
          <button
            onClick={handleManageAccount}
            disabled={true}
            className="tw-px-8 tw-py-3 tw-rounded-full tw-border tw-border-gray-300 tw-text-blue-600 tw-font-medium hover:tw-bg-blue-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {t("userAccountModal.manageAccount")}
          </button>
        </div>

        {/* Action buttons */}
        <div className="tw-flex tw-justify-end tw-gap-2 tw-mb-6 tw-pt-4 tw-border-t tw-border-gray-200">
          {/* <button
            onClick={handleManageAccount}
            className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
          >
            <svg
              className="tw-w-5 tw-h-5 tw-text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="tw-text-sm tw-text-gray-700">
              {t("userAccountModal.addAccount")}
            </span>
          </button> */}
          <button
            onClick={handleSignOut}
            className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
          >
            <svg
              className="tw-w-5 tw-h-5 tw-text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className="tw-text-sm tw-text-gray-700">
              {t("userAccountModal.signOut")}
            </span>
          </button>
        </div>

        {/* Footer links */}
        <div className="tw-flex tw-justify-center tw-gap-3 tw-text-xs tw-text-gray-600 tw-pt-2">
          <button
            className="hover:tw-underline disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            disabled={true}
          >
            {t("userAccountModal.privacyPolicy")}
          </button>
          <span>•</span>
          <button
            className="hover:tw-underline disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            disabled={true}
          >
            {t("userAccountModal.termsOfService")}
          </button>
        </div>
      </div>
    </div>
  );
}
